import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ButtonBase,
  Chip
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";

import type { Bot } from "@/data/mock-data";
import {
  deriveBotReportMetrics,
  summarizeBotPortfolio
} from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";
import type { BotSortKey } from "@/components/BotsFilterBar";

// ---- Column schema -------------------------------------------------------

/**
 * The 11 columns from the WHB Weekly Report, in the same order. Keep
 * labels short — the table gets narrow fast on mobile and the header
 * row is already capped at a single line of text.
 */
type ColumnId =
  | "bot"
  | "value"
  | "allocPct"
  | "dayPct"
  | "twrPct"
  | "dollarReturn"
  | "apyPct"
  | "sharpe"
  | "maxDDPct"
  | "netDeposits"
  | "contribPct"
  | "ageDays";

interface ColumnSpec {
  id: ColumnId;
  label: string;
  align: "left" | "right";
  /** Fixed pixel width — prevents the first-column sticky from breaking. */
  width: number;
  /** If truthy, clicking the header routes to `onSort` with this key. */
  sortKey?: BotSortKey;
}

const COLUMNS: ColumnSpec[] = [
  { id: "bot", label: "Bot", align: "left", width: 220 },
  { id: "value", label: "Value", align: "right", width: 120, sortKey: "value" },
  { id: "allocPct", label: "Alloc %", align: "right", width: 88, sortKey: "allocPct" },
  { id: "dayPct", label: "Day %", align: "right", width: 88, sortKey: "dayPct" },
  { id: "twrPct", label: "TWR", align: "right", width: 96, sortKey: "twrPct" },
  { id: "dollarReturn", label: "$ Return", align: "right", width: 120 },
  { id: "apyPct", label: "APY", align: "right", width: 96, sortKey: "apyPct" },
  { id: "sharpe", label: "Sharpe", align: "right", width: 82, sortKey: "sharpe" },
  { id: "maxDDPct", label: "Max DD", align: "right", width: 96, sortKey: "maxDDPct" },
  { id: "netDeposits", label: "Net Dep", align: "right", width: 120, sortKey: "netDeposits" },
  { id: "contribPct", label: "Contrib %", align: "right", width: 104, sortKey: "contribPct" },
  { id: "ageDays", label: "Age", align: "right", width: 84, sortKey: "ageDays" }
];

// ---- Component -----------------------------------------------------------

interface BotsAdvancedViewProps {
  /** Already filtered + sorted by the caller. */
  bots: Bot[];
  /** Current sort key, for header highlighting + arrow direction. */
  sortKey?: BotSortKey;
  sortDir?: "asc" | "desc";
  /** Called when the user taps a sortable header. */
  onSort?: (key: BotSortKey) => void;
  /** Route to bot detail — wired by the parent. */
  onBotClick?: (id: string) => void;
  /**
   * Whether to render the Portfolio summary row. Mirrors the Simple
   * view — show when we have more than one bot in the cohort.
   */
  showSummary?: boolean;
}

/**
 * 11-column advanced table matching the WHB Weekly Report layout.
 *
 * Layout:
 *  • First column (`Bot`) sticks during horizontal scroll — the
 *    bot name is the row identifier, so keeping it visible while the
 *    user swipes through metrics is essential. Header and body cells
 *    share `position: sticky; left: 0`.
 *  • Portfolio summary row renders as the first TableRow (above the
 *    individual bots), styled with a faint primary tint so it reads
 *    as a "total" rather than a regular row.
 *  • Percentage and dollar-return cells are sign-coloured to match
 *    the rest of the app's P&L treatment.
 *  • Column headers with a `sortKey` behave like buttons — tapping
 *    cycles through the parent's sort state.
 */
export function BotsAdvancedView({
  bots,
  sortKey,
  sortDir,
  onSort,
  onBotClick,
  showSummary = true
}: BotsAdvancedViewProps) {
  if (bots.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No bots match your filters.</Typography>
      </Box>
    );
  }

  const derived = deriveBotReportMetrics(bots);
  const summary = summarizeBotPortfolio(bots);

  return (
    <Box sx={{ pt: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {bots.length} {bots.length === 1 ? "bot" : "bots"}
        </Typography>
      </Stack>

      {/* Edge-to-edge + sticky-header table — mirrors the Orders
       *  Advanced view exactly (Sergey 2026-04-26). The `mx: -2`
       *  cancels the page's px: 2 padding so columns can use the full
       *  viewport width. `maxHeight` + overflow-y: auto turns the
       *  wrapper into a real Y scroll container so the sticky <th>s
       *  pin at the top while body rows scroll beneath. */}
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          mx: -2,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: (t) => alpha(t.palette.text.secondary, 0.3),
            borderRadius: 3
          }
        }}
      >
        <Table
          size="medium"
          sx={{
            minWidth: 1280,
            // Sergey 2026-04-27 — table typography sweep to body1
            // (1rem) so the values match the "Buying Power" baseline
            // that anchors the rest of the page. MUI's default
            // table-body font-size is 0.875rem; we override at the
            // table level so every TD inherits without per-cell sx
            // noise. Header rows keep their own smaller uppercase
            // treatment via the explicit fontSize on each TH.
            "& tbody td": { fontSize: "1rem", py: 1.5 }
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    // Slight bump from 11 → 12px so the header reads
                    // at a comfortable scale without overpowering the
                    // values. Letter-spacing kept at 0.3 for the
                    // uppercase column-label feel.
                    fontSize: 12,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    bgcolor: "background.paper",
                    width: col.width,
                    py: 1.25,
                    // Spread first (carries left:0 + z-index:2 + box-
                    // shadow for col 0), then layer the header-row
                    // pinning ON TOP so our values win where they
                    // overlap.
                    ...(i === 0 && stickyFirstCellSx),
                    position: "sticky",
                    top: 0,
                    // Corner cell beats both row-level sticky first
                    // cell (z-index 2) and other header cells when
                    // both axes are scrolling.
                    zIndex: i === 0 ? 3 : 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`
                  }}
                >
                  {col.sortKey && onSort ? (
                    <SortableHeader
                      label={col.label}
                      active={sortKey === col.sortKey}
                      dir={sortKey === col.sortKey ? sortDir : undefined}
                      align={col.align}
                      onClick={() => onSort(col.sortKey!)}
                    />
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showSummary && bots.length > 1 && (
              <PortfolioSummaryRow summary={summary} />
            )}
            {bots.map((b) => (
              <BotRow
                key={b.id}
                bot={b}
                allocPct={derived.get(b.id)?.allocPct ?? 0}
                contribPct={derived.get(b.id)?.contribPct ?? 0}
                ageDays={derived.get(b.id)?.ageDays ?? 0}
                onClick={onBotClick ? () => onBotClick(b.id) : undefined}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ---- Sortable header -----------------------------------------------------

function SortableHeader({
  label,
  active,
  dir,
  align,
  onClick
}: {
  label: string;
  active: boolean;
  dir?: "asc" | "desc";
  align: "left" | "right";
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        typography: "inherit",
        color: active ? "primary.main" : "text.secondary",
        fontWeight: 600,
        fontSize: "inherit",
        letterSpacing: "inherit",
        textTransform: "inherit",
        px: 0.5,
        py: 0.25,
        borderRadius: 0.5,
        // Flip the icon side so it lands next to the value column
        // edge — on right-aligned columns that's the right side.
        flexDirection: align === "right" ? "row-reverse" : "row",
        gap: 0.25,
        "&:hover": {
          bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
          color: "primary.main"
        }
      }}
    >
      {active &&
        (dir === "asc" ? (
          <ArrowUpwardRounded sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardRounded sx={{ fontSize: 14 }} />
        ))}
      {label}
    </ButtonBase>
  );
}

// ---- Portfolio summary row -----------------------------------------------

function PortfolioSummaryRow({
  summary
}: {
  summary: ReturnType<typeof summarizeBotPortfolio>;
}) {
  return (
    <TableRow
      sx={(t) => ({
        bgcolor: alpha(t.palette.primary.main, 0.06),
        "& td": { fontWeight: 700 }
      })}
    >
      <TableCell
        sx={{
          ...stickyFirstCellSx,
          whiteSpace: "nowrap",
          // Sticky cells need an OPAQUE background — otherwise content
          // from the scrolling columns on the right bleeds through the
          // 6% primary tint. Layer the tint on top of background.paper
          // via backgroundImage so the visual result matches the
          // translucent row while the cell itself stays opaque.
          bgcolor: "background.paper",
          backgroundImage: (t) =>
            `linear-gradient(${alpha(t.palette.primary.main, 0.06)}, ${alpha(
              t.palette.primary.main,
              0.06
            )})`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main"
            }}
          >
            <SmartToyOutlined sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Portfolio
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", display: "block", lineHeight: 1.2 }}
            >
              {summary.count} bots
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(summary.totalValue)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        100%
      </TableCell>
      <TableCell align="right">
        <SignedPct value={summary.weightedDayChangePct} />
      </TableCell>
      <TableCell align="right">
        <SignedPct value={summary.weightedTwrPct} />
      </TableCell>
      <TableCell align="right">
        <SignedDollar value={summary.totalDollarReturn} />
      </TableCell>
      <TableCell align="right">
        <SignedPct value={summary.weightedApyPct} />
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {summary.weightedSharpe.toFixed(2)}
      </TableCell>
      <TableCell align="right">
        <SignedPct value={summary.worstMaxDDPct} alwaysColored />
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(summary.totalNetDeposits)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        100%
      </TableCell>
      <TableCell align="right" sx={{ color: "text.secondary" }}>
        —
      </TableCell>
    </TableRow>
  );
}

// ---- Bot row -------------------------------------------------------------

function BotRow({
  bot,
  allocPct,
  contribPct,
  ageDays,
  onClick
}: {
  bot: Bot;
  allocPct: number;
  contribPct: number;
  ageDays: number;
  onClick?: () => void;
}) {
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:last-of-type td": { borderBottom: 0 }
      }}
    >
      <TableCell sx={{ ...stickyFirstCellSx, bgcolor: "background.paper" }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <BotDot status={bot.status} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 220
              }}
            >
              {bot.name}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", lineHeight: 1.2 }}
              >
                {bot.tickers.slice(0, 3).join(", ")}
                {bot.tickers.length > 3 && ` +${bot.tickers.length - 3}`}
              </Typography>
              {bot.status === "PAUSED" && (
                <Chip
                  label="Paused"
                  size="small"
                  sx={(t) => ({
                    height: 20,
                    bgcolor: alpha(t.palette.warning.main, 0.15),
                    color: t.palette.warning.main,
                    fontWeight: 600,
                    "& .MuiChip-label": { px: 1, fontSize: 11 }
                  })}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(bot.currentValue)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {(allocPct * 100).toFixed(1)}%
      </TableCell>
      <TableCell align="right">
        <SignedPct value={bot.dayChangePct} />
      </TableCell>
      <TableCell align="right">
        <SignedPct value={bot.twrPct} />
      </TableCell>
      <TableCell align="right">
        <SignedDollar value={bot.allTimeGain} />
      </TableCell>
      <TableCell align="right">
        <SignedPct value={bot.apyPct} />
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {bot.sharpe.toFixed(2)}
      </TableCell>
      <TableCell align="right">
        {/* Max DD is always negative-coloured regardless of magnitude —
            a "0%" drawdown is a rare edge case and painting it neutral
            would look like a bug next to adjacent red rows. */}
        <SignedPct value={bot.maxDDPct} alwaysColored />
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(bot.netDeposits)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {(contribPct * 100).toFixed(1)}%
      </TableCell>
      <TableCell
        align="right"
        sx={{ fontFeatureSettings: '"tnum" 1', color: "text.secondary" }}
      >
        {formatAgeCompact(ageDays)}
      </TableCell>
    </TableRow>
  );
}

// ---- Cell components -----------------------------------------------------

function SignedPct({
  value,
  alwaysColored
}: {
  value: number;
  /** Colour even when value is 0 (used for Max DD so "0%" still
   *  paints as 'error' to stay visually consistent with the column). */
  alwaysColored?: boolean;
}) {
  const positive = value > 0;
  const negative = value < 0;
  const color = (t: Theme) => {
    if (positive) return t.palette.success.main;
    if (negative || alwaysColored) return t.palette.error.main;
    return t.palette.text.primary;
  };
  const sign = positive ? "+" : negative ? "−" : "";
  return (
    <Typography
      variant="body1"
      sx={{ color, fontWeight: 600, fontFeatureSettings: '"tnum" 1' }}
    >
      {sign}
      {Math.abs(value * 100).toFixed(2)}%
    </Typography>
  );
}

function SignedDollar({ value }: { value: number }) {
  const positive = value > 0;
  const negative = value < 0;
  const color = (t: Theme) =>
    positive
      ? t.palette.success.main
      : negative
        ? t.palette.error.main
        : t.palette.text.primary;
  const sign = positive ? "+" : negative ? "−" : "";
  return (
    <Typography
      variant="body1"
      sx={{ color, fontWeight: 600, fontFeatureSettings: '"tnum" 1' }}
    >
      {sign}
      {formatUSD(Math.abs(value))}
    </Typography>
  );
}

// ---- Bot icon -------------------------------------------------------------

/**
 * WHB-native line-art bot icon — same treatment used in BotsSimpleView
 * and BotDetailDrawer (Sergey 2026-04-26). No coloured background; the
 * icon inherits foreground colour and dims to text.disabled when the
 * bot is paused.
 */
function BotDot({ status }: { status: Bot["status"] }) {
  const muted = status === "PAUSED";
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: muted ? "text.disabled" : "text.primary"
      }}
    >
      <SmartToyOutlined sx={{ fontSize: 22 }} />
    </Box>
  );
}

// ---- Sticky first-column sx ---------------------------------------------

const stickyFirstCellSx = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  boxShadow: (t: Theme) => `inset -1px 0 0 0 ${t.palette.divider}`
};

// ---- Format helpers ------------------------------------------------------

/**
 * Compact age for the column — 3-char max most of the time. "1y 3mo"
 * is rare enough that an "1y+" label reads fine for the column-width
 * budget.
 */
function formatAgeCompact(days: number): string {
  if (days < 7) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}
